import { useEffect } from "react";
import "@/i18n";
import i18n from "@/i18n";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Layout } from "@/components/site/Layout";
import HomePage from "@/pages/HomePage";
import DashboardPage from "@/pages/DashboardPage";
import IndustriesPage from "@/pages/IndustriesPage";
import LocalizationPage from "@/pages/LocalizationPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import InsightsPage from "@/pages/InsightsPage";
import InsightDetailPage from "@/pages/InsightDetailPage";
import AdminLandingPage from "@/pages/AdminLandingPage";
import AdminInsightsPage from "@/pages/AdminInsightsPage";
import AdminLeadsPage from "@/pages/AdminLeadsPage";
import EngagementPage from "@/pages/EngagementPage";
import PricingPage from "@/pages/PricingPage";
import NotFoundPage from "@/pages/NotFoundPage";
import TermsPage from "@/pages/legal/TermsPage";
import PrivacyPage from "@/pages/legal/PrivacyPage";
import RefundPage from "@/pages/legal/RefundPage";
import KycAmlPage from "@/pages/legal/KycAmlPage";
import { detectLangFromPath } from "@/lib/langRoute";
import { devLog } from "@/lib/devLog";
import { trackPageView, trackCustomEvent, bootstrapMetaPixelWithConsent } from "@/lib/metaPixel";
import { bootstrapLinkedInWithConsent } from "@/lib/linkedInInsight";
import { initConsentDefaults } from "@/lib/consent";

// Legacy /insights/:slug deep link — redirect to canonical /blog/:slug.
const LegacyBlogRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/blog/${slug}`} replace />;
};

/**
 * Fires Meta Pixel PageView + GA4 page_view on every SPA route change, plus
 * a dedicated `HomepageView` custom event when the user lands on `/`. The
 * initial page view from index.html covers the very first HTML load; this
 * hook covers subsequent client-side navigations.
 */
const PixelRouteTracker = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageView();
    // Google Analytics 4 SPA page_view.
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      try {
        window.gtag("event", "page_view", {
          page_path: location.pathname + location.search,
          page_location: window.location.href,
          page_title: document.title,
        });
      } catch (e) {
        // GA4 gtag can throw when blocked by extensions/consent tooling.
        // The tracking failure must never break SPA navigation.
        devLog.warn("PixelRouteTracker: gtag page_view failed", e);
      }
    }
    if (location.pathname === "/" || location.pathname === "") {
      trackCustomEvent("HomepageView", { path: location.pathname || "/" });
    }
  }, [location.pathname, location.search]);
  return null;
};

function App() {
  // Detect language from URL prefix once at boot. The BrowserRouter `basename`
  // ensures all internal <Link to="/path"> calls automatically keep the prefix.
  const { prefix, lang } = detectLangFromPath();

  useEffect(() => {
    // Bootstrap all marketing trackers once, at app boot. Both bootstraps
    // are internally consent-gated:
    //   • If the visitor already granted marketing consent on a prior
    //     visit, they init immediately.
    //   • Otherwise they subscribe to consent changes and lazy-init the
    //     moment "Accept All" is clicked in CookieConsent.jsx.
    // Never fires during the react-snap prerender pass (guarded via
    // navigator.userAgent === "ReactSnap" inside each module).
    bootstrapMetaPixelWithConsent();
    bootstrapLinkedInWithConsent();
  }, []);

  useEffect(() => {
    // Only FORCE a language when the URL explicitly signals one via prefix
    // (e.g. `/de/pricing`, `/ru/`). Without a prefix, we defer to i18next's
    // detection chain (localStorage → geo → navigator → en) so that:
    //   • a saved user choice persists across visits, and
    //   • a first-time visitor from Germany / Russia / Vietnam / etc. gets
    //     their local language via the /api/geo Cloudflare hint.
    // If we always wrote `en` here, both mechanisms would be defeated.
    if (prefix) {
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang);
      }
      try { localStorage.setItem("dc_lang", lang); }
      catch (e) { devLog.warn("App: failed to persist language", e); }
    }
  }, [prefix, lang]);

  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter basename={prefix ? `/${prefix}` : ""}>
        <PixelRouteTracker />
        <Routes>
          <Route element={<Layout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/solutions" element={<Navigate to="/dashboard" replace />} />
            <Route path="/localization" element={<LocalizationPage />} />
            <Route path="/industries" element={<IndustriesPage />} />
            <Route path="/engagement" element={<EngagementPage />} />
            <Route path="/pricing" element={<PricingPage />} />

            {/* Blog (canonical) */}
            <Route path="/blog" element={<InsightsPage />} />
            <Route path="/blog/:slug" element={<InsightDetailPage />} />

            {/* Legacy /insights paths — keep working for any external backlinks */}
            <Route path="/insights" element={<Navigate to="/blog" replace />} />
            <Route path="/insights/:slug" element={<LegacyBlogRedirect />} />

            {/* Admin (intentionally undocumented URL prefix) */}
            <Route path="/admin-clan" element={<AdminLandingPage />} />
            <Route path="/admin-clan/blog" element={<AdminInsightsPage />} />
            <Route path="/admin-clan/leads" element={<AdminLeadsPage />} />

            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />

            {/* Legal — canonical URLs matching dannyclans.com counterparts */}
            <Route path="/terms-of-service" element={<TermsPage />} />
            <Route path="/privacy-policy"   element={<PrivacyPage />} />
            <Route path="/refund-policy"    element={<RefundPage />} />
            <Route path="/kyc-aml-policy"   element={<KycAmlPage />} />
            {/* Common legacy paths — 301 to canonical legal URLs */}
            <Route path="/terms"   element={<Navigate to="/terms-of-service" replace />} />
            <Route path="/privacy" element={<Navigate to="/privacy-policy"   replace />} />
            <Route path="/refund"  element={<Navigate to="/refund-policy"    replace />} />
            <Route path="/kyc"     element={<Navigate to="/kyc-aml-policy"   replace />} />
            <Route path="/aml"     element={<Navigate to="/kyc-aml-policy"   replace />} />

            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
